import { useTranslation } from 'react-i18next'
import { Brain, Lightbulb } from 'lucide-react'

interface SalesPsychologyCardProps {
  data: Record<string, unknown>
}

const LABEL_KEYS: Record<string, string> = {
  benefit_first_ratio: 'benefitFirstRatio',
  story_arc: 'storyArc',
  emotional_triggers_found: 'emotionalTriggers',
  hook_present: 'hookPresent',
  power_words: 'powerWords',
  positioning_clarity: 'positioningClarity',
  emotional_appeal: 'emotionalAppeal',
  perception_gap: 'perceptionGap',
  sells_experience: 'sellsExperience',
  trust_elements: 'trustElements',
  social_proof_elements: 'socialProofElements',
  call_to_action: 'callToAction',
}

const STATUS_STYLES: Record<string, string> = {
  present: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  clear: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  weak: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  vague: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  missing: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
}

export function SalesPsychologyCard({ data }: SalesPsychologyCardProps) {
  const { t } = useTranslation()
  if (!data) return null

  const recommendation = data.recommendation as string | undefined
  const entries = Object.entries(data).filter(
    ([k, v]) => k !== 'recommendation' && v !== null && v !== undefined && v !== '',
  )
  if (entries.length === 0 && !recommendation) return null

  return (
    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800 p-4 space-y-3">
      <h4 className="flex items-center gap-2 text-sm font-medium text-primary-800 dark:text-primary-300">
        <Brain className="w-4 h-4" />
        {t('amazonListing.report.salesPsychology')}
      </h4>
      <div className="space-y-3">
        {entries.map(([key, value]) => (
          <PsychMetric key={key} fieldKey={key} value={value} />
        ))}
      </div>
      {recommendation && <RecommendationBox text={recommendation} />}
    </div>
  )
}

function RecommendationBox({ text }: { text: string }) {
  const { t } = useTranslation()
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg border border-orange-200 dark:border-orange-800 p-3">
      <div className="flex items-start gap-2">
        <Lightbulb className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
        <div>
          <span className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wide">
            {t('amazonListing.salesPsych.toDo')}
          </span>
          <p className="text-sm text-neutral-700 dark:text-neutral-300 mt-0.5 whitespace-pre-line">{text}</p>
        </div>
      </div>
    </div>
  )
}

function PsychMetric({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  const { t } = useTranslation()
  const p = (k: string) => t(`amazonListing.salesPsych.${k}`)
  const labelKey = LABEL_KEYS[fieldKey]
  const label = labelKey ? p(labelKey) : fieldKey.replace(/_/g, ' ')
  const tip = labelKey ? p(`${labelKey}Tip`) : ''

  if (typeof value === 'number' && value >= 0 && value <= 1) {
    return <RatioMetric label={label} tip={tip} value={value} />
  }
  if (typeof value === 'string' && STATUS_STYLES[value]) {
    return (
      <MetricRow label={label} tip={tip}>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${STATUS_STYLES[value]}`}>
          {p(`status.${value}`)}
        </span>
      </MetricRow>
    )
  }
  if (typeof value === 'boolean') {
    const style = value
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    return (
      <MetricRow label={label} tip={tip}>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${style}`}>
          {value ? p('statusYes') : p('statusNo')}
        </span>
      </MetricRow>
    )
  }
  if (Array.isArray(value) && value.length > 0) {
    return (
      <div>
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">{label}</span>
        {tip && <p className="text-xs text-primary-600/70 dark:text-primary-400/70">{tip}</p>}
        <div className="flex flex-wrap gap-1 mt-1.5">
          {value.map((v, i) => {
            const translated = p(`trigger.${String(v)}`)
            const display = translated.startsWith('amazonListing.') ? String(v) : translated
            return (
              <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                {display}
              </span>
            )
          })}
        </div>
      </div>
    )
  }
  if (typeof value === 'string') {
    return (
      <div>
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">{label}</span>
        {tip && <p className="text-xs text-primary-600/70 dark:text-primary-400/70">{tip}</p>}
        <p className="text-sm text-neutral-700 dark:text-neutral-300 mt-0.5">{value}</p>
      </div>
    )
  }
  return null
}

function RatioMetric({ label, tip, value }: { label: string; tip: string; value: number }) {
  const pct = Math.round(value * 100)
  const barColor = pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500'
  const textColor = pct >= 70 ? 'text-green-600' : pct >= 40 ? 'text-yellow-600' : 'text-red-600'
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">{label}</span>
        <span className={`text-sm font-bold ${textColor}`}>{pct}%</span>
      </div>
      <div className="h-2 bg-primary-100 dark:bg-primary-900/40 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      {tip && <p className="text-xs text-primary-600/70 dark:text-primary-400/70 mt-1">{tip}</p>}
    </div>
  )
}

function MetricRow({ label, tip, children }: { label: string; tip: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">{label}</span>
        {tip && <p className="text-xs text-primary-600/70 dark:text-primary-400/70">{tip}</p>}
      </div>
      {children}
    </div>
  )
}
